/**
 * Ambient IoT per-SKU identity schemas — IOT-Z20-AMBIENT-SCAFFOLD-1.
 *
 * Substrate scaffold for 2028-2030 ambient IoT (Wiliot / EnOcean / PragmatIC).
 * Patent priority anchor: F-AMBIENT-IOT-PER-SKU-001.
 *
 * Two surfaces are exported:
 *   1. ItemIdentitySchema — optional sub-document on iot_readings telemetry
 *      records that lets ambient beacon reads carry SKU-level identity
 *      alongside the canonical NormalizedReading payload.
 *   2. ItemIdentityRegistryDocSchema — the per-tenant
 *      `item_identity_registry` collection document. CF-only writes
 *      (HAL-WRITE-PATH).
 *
 * Backward compatibility: the iot_readings extension is purely additive —
 * existing telemetry that does not carry `itemIdentity` continues to validate
 * and continues to be read by all current consumers (hooks, services, demo seed).
 */
import { z } from 'zod';

// ── Frozen enums ──────────────────────────────────────────────────────────────

export const ENERGY_HARVEST_SOURCES = Object.freeze({
  RF:      'rf',
  THERMAL: 'thermal',
  KINETIC: 'kinetic',
  BATTERY: 'battery',
});

export const TAG_VENDORS = Object.freeze({
  WILIOT:    'wiliot',
  ENOCEAN:   'enocean',
  PRAGMATIC: 'pragmatic',
  GENERIC:   'generic',
});

export const HIERARCHY_LEVELS = Object.freeze({
  ITEM:      'item',
  CARTON:    'carton',
  PALLET:    'pallet',
  CONTAINER: 'container',
  SHIPMENT:  'shipment',
});

export const ITEM_LIFECYCLE_STATUS = Object.freeze({
  MANUFACTURED: 'manufactured',
  IN_TRANSIT:   'in_transit',
  IN_STORAGE:   'in_storage',
  DELIVERED:    'delivered',
  RETIRED:      'retired',
});

export const EnergyHarvestSourceEnum = z.enum(Object.values(ENERGY_HARVEST_SOURCES));
export const TagVendorEnum           = z.enum(Object.values(TAG_VENDORS));
export const HierarchyLevelEnum      = z.enum(Object.values(HIERARCHY_LEVELS));
export const ItemLifecycleStatusEnum = z.enum(Object.values(ITEM_LIFECYCLE_STATUS));

// ── Building blocks ───────────────────────────────────────────────────────────

export const ParentHierarchyEntrySchema = z.object({
  level: HierarchyLevelEnum,
  id:    z.string().min(1),
});

export const ManufactureChainStepSchema = z.object({
  step:      z.string().min(1),
  tenant:    z.string().min(1),
  timestamp: z.string().min(1), // ISO 8601
});

export const CustodyChainEntrySchema = z.object({
  tenant:     z.string().min(1),
  receivedAt: z.string().min(1),
  releasedAt: z.string().nullable().optional(),
  location:   z.string().nullable().optional(),
});

export const AmbientTagSpecSchema = z.object({
  vendor:                TagVendorEnum,
  tagId:                 z.string().min(1),
  energyHarvestProfile:  EnergyHarvestSourceEnum,
  expectedReadFrequency: z.string().min(1), // e.g. "1/hour", "1/min"
});

// ── ItemIdentity — optional sub-document on iot_readings ──────────────────────

export const ItemIdentitySchema = z.object({
  itemUuid:               z.string().min(1),
  parentHierarchyChain:   z.array(ParentHierarchyEntrySchema).default([]),
  currentCustodianTenant: z.string().min(1),
  energyHarvestSource:    EnergyHarvestSourceEnum,
  tagVendor:              TagVendorEnum,
  manufactureChain:       z.array(ManufactureChainStepSchema).default([]),
});

// ── item_identity_registry doc shape ──────────────────────────────────────────

export const ItemIdentityRegistryDocSchema = z
  .object({
    itemUuid:         z.string().min(1),
    tenantId:         z.string().min(1),
    parentHierarchy:  z.array(ParentHierarchyEntrySchema).default([]),
    authenticityProof: z.string().nullable().optional(),
    ambientTagSpec:   AmbientTagSpecSchema,
    custodyChain:     z.array(CustodyChainEntrySchema).default([]),
    lifecycleStatus:  ItemLifecycleStatusEnum,
    createdAt:        z.string().min(1),
  })
  .passthrough();

// ── Reconcile-hierarchy input shape (Phase-2 stub) ────────────────────────────

export const ReconcileHierarchyReadSchema = z.object({
  itemUuid:  z.string().min(1),
  tagId:     z.string().min(1).optional(),
  observedAt: z.string().min(1),
  proximity: z.array(z.object({
    siblingItemUuid: z.string().min(1),
    rssi:            z.number().optional(),
  })).default([]),
  motionSignature: z.string().nullable().optional(),
  observerDeviceId: z.string().min(1).optional(),
});

// ── Anomaly-detection input shape ─────────────────────────────────────────────

export const ItemAnomalyInputSchema = z.object({
  itemUuid:        z.string().min(1),
  currentLocation: z.string().min(1),
  expectedChain:   z.array(CustodyChainEntrySchema).default([]),
});
